/**
 * Manages audio input from microphone using Web Audio API
 */

export type AudioCallback = (buffer: Float32Array) => void;

export class AudioInputManager {
  private audioContext: AudioContext | null = null;
  private analyserNode: AnalyserNode | null = null;
  private mediaStream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private animationFrameId: number | null = null;
  private bufferSize: number;
  private callback: AudioCallback | null = null;

  constructor(bufferSize = 4096) {
    this.bufferSize = bufferSize;
  }

  /**
   * Initialize and start capturing audio from microphone
   */
  async start(callback: AudioCallback): Promise<void> {
    this.callback = callback;

    try {
      // Check for mediaDevices support
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia is not supported in this browser');
      }

      // Create audio context (with webkit prefix support for older Safari)
      const AudioContextClass = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        throw new Error('Web Audio API is not supported in this browser');
      }

      const audioContext = new AudioContextClass();
      this.audioContext = audioContext;

      // Safari requires explicit resume of AudioContext
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Request microphone access with simpler constraints for better compatibility
      let mediaStream: MediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
          video: false,
        });
      } catch (mediaError) {
        // Try again with basic constraints if advanced ones fail
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false,
        });
      }
      this.mediaStream = mediaStream;

      // Create analyser node
      const analyserNode = audioContext.createAnalyser();
      analyserNode.fftSize = this.bufferSize * 2;
      analyserNode.smoothingTimeConstant = 0;
      this.analyserNode = analyserNode;

      // Connect microphone to analyser
      const sourceNode = audioContext.createMediaStreamSource(mediaStream);
      sourceNode.connect(analyserNode);
      this.sourceNode = sourceNode;

      // Ensure AudioContext is running (Safari may suspend it)
      if (audioContext.state !== 'running') {
        await audioContext.resume();
      }

      // Start processing audio
      this.processAudio();
    } catch (error) {
      // Clean up on error
      this.stop();
      throw new Error(
        `Failed to access microphone: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Stop capturing audio and release resources
   */
  stop(): void {
    // Cancel animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Disconnect and close audio nodes
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.analyserNode) {
      this.analyserNode.disconnect();
      this.analyserNode = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    // Stop media stream
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    this.callback = null;
  }

  /**
   * Process audio in a loop using requestAnimationFrame
   */
  private processAudio(): void {
    if (!this.analyserNode || !this.callback) {
      return;
    }

    // Get time-domain data
    const buffer = new Float32Array(this.analyserNode.fftSize);
    this.analyserNode.getFloatTimeDomainData(buffer);

    // Debug: Log RMS every 60 frames (~1 second)
    if (typeof (window as any).__audioFrameCount === 'undefined') {
      (window as any).__audioFrameCount = 0;
    }
    (window as any).__audioFrameCount++;

    if ((window as any).__audioFrameCount % 60 === 0) {
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) {
        sum += buffer[i] * buffer[i];
      }
      const rms = Math.sqrt(sum / buffer.length);
      console.log(`[AudioInput] RMS: ${rms.toFixed(4)}, Buffer size: ${buffer.length}`);
    }

    // Send to callback
    this.callback(buffer);

    // Schedule next frame
    this.animationFrameId = requestAnimationFrame(() => this.processAudio());
  }

  /**
   * Get the sample rate of the audio context
   */
  getSampleRate(): number {
    return this.audioContext?.sampleRate || 44100;
  }

  /**
   * Check if audio is currently being captured
   */
  isActive(): boolean {
    return this.audioContext !== null && this.audioContext.state === 'running';
  }

  /**
   * Get the current buffer size
   */
  getBufferSize(): number {
    return this.bufferSize;
  }
}
